import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Select,
  MenuItem,
  Avatar,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Snackbar,
  Alert,
  Card,
  CardContent,
  CircularProgress,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  Search as SearchIcon,
  ArrowBack as ArrowBackIcon,
  People as PeopleIcon,
  Pending as PendingIcon,
  PersonAdd as PersonAddIcon,
  Key as KeyIcon,
  Verified as VerifiedIcon,
  AdminPanelSettings as AdminIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import {
  collection,
  onSnapshot,
  query,
  orderBy,
  doc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  Timestamp,
} from "firebase/firestore";
import { db } from "firebaseConfig";
import { useAuth } from "contexts/AuthContext";
import { UserProfile } from "types/user";

/* ---------------------------------------------------------------- */
/* Constants & helpers                                                */
/* ---------------------------------------------------------------- */

const ROLE_LABELS: Record<number, { label: string; color: "error" | "warning" | "info" | "success" }> = {
  "-1": { label: "Chờ duyệt", color: "error" },
  "0": { label: "Thành viên", color: "warning" },
  "1": { label: "Premium", color: "info" },
  "2": { label: "Quản trị", color: "success" },
};

const ROLE_ORDER: Record<number, number> = { 2: 0, 1: 1, 0: 2, [-1]: 3 };

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

function formatLastLogin(lastLoginAt: Timestamp | null): { label: string; color: string; expired: boolean } {
  if (!lastLoginAt) return { label: "—", color: "#475569", expired: false };
  try {
    const d = lastLoginAt.toDate();
    const ms = d.getTime();
    const expired = Date.now() - ms > SEVEN_DAYS_MS;
    const label = d.toLocaleDateString("vi-VN", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
    return { label, color: expired ? "#f87171" : "#64748b", expired };
  } catch {
    return { label: "—", color: "#475569", expired: false };
  }
}

const ROLE_CONFIG: Record<number, { bg: string; border: string; icon: React.ReactNode }> = {
  "-1": { bg: "rgba(239,68,68,0.08)", border: "#ef4444", icon: <PendingIcon fontSize="small" /> },
  "0": { bg: "rgba(245,158,11,0.08)", border: "#f59e0b", icon: <PersonAddIcon fontSize="small" /> },
  "1": { bg: "rgba(59,130,246,0.08)", border: "#3b82f6", icon: <KeyIcon fontSize="small" /> },
  "2": { bg: "rgba(16,185,129,0.08)", border: "#10b981", icon: <AdminIcon fontSize="small" /> },
};

const STAT_COLORS = [
  { bg: "linear-gradient(135deg, #ef4444, #dc2626)", hover: "linear-gradient(135deg, #f87171, #ef4444)", text: "#fecaca" },
  { bg: "linear-gradient(135deg, #f59e0b, #d97706)", hover: "linear-gradient(135deg, #fbbf24, #f59e0b)", text: "#fde68a" },
  { bg: "linear-gradient(135deg, #3b82f6, #2563eb)", hover: "linear-gradient(135deg, #60a5fa, #3b82f6)", text: "#bfdbfe" },
  { bg: "linear-gradient(135deg, #10b981, #059669)", hover: "linear-gradient(135deg, #34d399, #10b981)", text: "#a7f3d0" },
];

/* ---------------------------------------------------------------- */
/* StatCard                                                         */
/* ---------------------------------------------------------------- */

function StatCard({ icon, label, value, gradient, hoverGradient, textColor }: {
  icon: React.ReactNode;
  label: string;
  value: number | string;
  gradient: string;
  hoverGradient: string;
  textColor: string;
}) {
  const [hovered, setHovered] = useState(false);
  return (
    <Card
      sx={{
        flex: 1,
        minWidth: 180,
        background: gradient,
        border: "none",
        transition: "all 0.25s cubic-bezier(0.4,0,0.2,1)",
        transform: hovered ? "translateY(-3px)" : "none",
        "&:hover": { background: hoverGradient, boxShadow: "0 12px 28px rgba(0,0,0,0.35)" },
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <CardContent sx={{ display: "flex", alignItems: "center", gap: 2, p: 2 }}>
        <Box sx={{ color: "rgba(255,255,255,0.7)" }}>{icon}</Box>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, lineHeight: 1, color: "#fff" }}>
            {value}
          </Typography>
          <Typography variant="caption" sx={{ color: textColor }}>
            {label}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
}

/* ---------------------------------------------------------------- */
/* UserAvatar — safe avatar with fallback initials                  */
/* ---------------------------------------------------------------- */

function UserAvatar({ photoURL, displayName, borderColor }: { photoURL?: string; displayName: string; borderColor?: string }) {
  if (photoURL) {
    return <Avatar src={photoURL} sx={{ width: 40, height: 40, borderColor, borderWidth: borderColor ? 2 : 0, borderStyle: "solid" }} />;
  }
  const initials = displayName.split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase();
  return (
    <Avatar
      sx={{
        width: 40,
        height: 40,
        background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
        color: "#fff",
        fontSize: "0.8rem",
        fontWeight: 600,
      }}
    >
      {initials}
    </Avatar>
  );
}

/* ---------------------------------------------------------------- */
/* AdminPage                                                        */
/* ---------------------------------------------------------------- */

export default function AdminPage() {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<number | "all">("all");
  const [search, setSearch] = useState("");
  const [orderByField, setOrderBy] = useState<"createdAt" | "role">("createdAt");
  const [order, setOrder] = useState<"asc" | "desc">("desc");
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: "success" | "error" }>({
    open: false, message: "", severity: "success",
  });
  const [updating, setUpdating] = useState<string | null>(null);

  useEffect(() => {
    const q = query(collection(db, "users"), orderBy("createdAt", "desc"));
    const unsub = onSnapshot(
      q,
      (snap) => {
        setUsers(snap.docs.map((d) => ({ uid: d.id, ...d.data() } as UserProfile)));
        setLoading(false);
      },
      () => {
        setLoading(false);
        setSnackbar({ open: true, message: "Không thể tải danh sách users", severity: "error" });
      }
    );
    return () => unsub();
  }, []);

  const handleRoleChange = async (uid: string, newRole: number) => {
    if (uid === profile?.uid) {
      setSnackbar({ open: true, message: "Không thể thay đổi role của chính mình", severity: "error" });
      return;
    }
    setUpdating(uid);
    try {
      await updateDoc(doc(db, "users", uid), {
        role: newRole, updatedAt: serverTimestamp(),
        approvedBy: profile?.uid ?? null, approvedAt: serverTimestamp(),
      });
      setSnackbar({ open: true, message: "Cập nhật thành công", severity: "success" });
    } catch { setSnackbar({ open: true, message: "Lỗi khi cập nhật", severity: "error" }); }
    setUpdating(null);
  };

  const handleDelete = async (uid: string) => {
    if (uid === profile?.uid) {
      setSnackbar({ open: true, message: "Không thể xóa chính mình", severity: "error" });
      return;
    }
    if (!window.confirm("Xóa user này khỏi Firestore? (Firebase Auth account không bị xóa)")) return;
    try {
      await deleteDoc(doc(db, "users", uid));
      setSnackbar({ open: true, message: "Đã xóa", severity: "success" });
    } catch { setSnackbar({ open: true, message: "Lỗi khi xóa", severity: "error" }); }
  };

  const filteredUsers = useMemo(() => {
    return users
      .filter((u) => filter === "all" || u.role === filter)
      .filter((u) =>
        u.displayName?.toLowerCase().includes(search.toLowerCase()) ||
        u.email?.toLowerCase().includes(search.toLowerCase())
      )
      .sort((a, b) => {
        const mul = order === "asc" ? 1 : -1;
        if (orderByField === "role") return (ROLE_ORDER[a.role] ?? 99) > (ROLE_ORDER[b.role] ?? 99) ? mul : -mul;
        const dateA = a.createdAt?.toMillis?.() ?? 0;
        const dateB = b.createdAt?.toMillis?.() ?? 0;
        return dateA > dateB ? mul : -mul;
      });
  }, [users, filter, search, orderByField, order]);

  /* stat counts */
  const pendingCount = users.filter((u) => u.role === -1).length;
  const memberCount = users.filter((u) => u.role === 0).length;
  const premiumCount = users.filter((u) => u.role === 1).length;
  const adminCount = users.filter((u) => u.role === 2).length;

  const sortedLabel = orderByField === "role"
    ? (order === "asc" ? "Role ↑" : "Role ↓")
    : (order === "asc" ? "Ngày tạo ↑" : "Ngày tạo ↓");

  /* ---------------------------------------------------------------- */
  /* Render                                                           */
  /* ---------------------------------------------------------------- */

  return (
    <Box sx={{ p: { xs: 2, sm: 4 }, maxWidth: 1280, mx: "auto", minHeight: "100vh" }}>
      {/* ---------- Header ---------- */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 4 }}>
        <IconButton onClick={() => navigate(-1)} sx={{ color: "#64748b" }}>
          <ArrowBackIcon />
        </IconButton>
        <Box
          sx={{
            width: 48, height: 48, borderRadius: 2,
            background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: "0 4px 14px rgba(99,102,241,0.35)",
          }}
        >
          <PeopleIcon sx={{ color: "#fff", fontSize: 24 }} />
        </Box>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: "#f1f5f9", lineHeight: 1.2 }}>
            Quản trị người dùng
          </Typography>
          <Typography variant="caption" sx={{ color: "#475569" }}>
            Kiểm soát tài khoản, phân quyền & giám sát hoạt động
          </Typography>
        </Box>
      </Box>

      {/* ---------- Stats ---------- */}
      <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
        <StatCard icon={<PendingIcon />} label="Chờ duyệt" value={pendingCount} gradient={STAT_COLORS[0].bg} hoverGradient={STAT_COLORS[0].hover} textColor={STAT_COLORS[0].text} />
        <StatCard icon={<PersonAddIcon />} label="Thành viên" value={memberCount} gradient={STAT_COLORS[1].bg} hoverGradient={STAT_COLORS[1].hover} textColor={STAT_COLORS[1].text} />
        <StatCard icon={<KeyIcon />} label="Premium" value={premiumCount} gradient={STAT_COLORS[2].bg} hoverGradient={STAT_COLORS[2].hover} textColor={STAT_COLORS[2].text} />
        <StatCard icon={<AdminIcon />} label="Quản trị" value={adminCount} gradient={STAT_COLORS[3].bg} hoverGradient={STAT_COLORS[3].hover} textColor={STAT_COLORS[3].text} />
      </Box>

      {/* ---------- Filters ---------- */}
      <Paper
        sx={{
          p: 2, mb: 2,
          background: "#0f0d1e",
          border: "1px solid #1e1a3a",
          borderRadius: 2,
        }}
      >
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
          <TextField
            size="small" placeholder="Tìm theo tên hoặc email..."
            value={search} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
            fullWidth
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: "#475569" }} /></InputAdornment>,
              sx: { color: "#f1f5f9", borderRadius: 1,
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "#1e1a3a" },
                "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#312e81" },
              },
            }}
          />
          <Select size="small" value={filter} onChange={(e) => setFilter(e.target.value as number | "all")}
            sx={{
              minWidth: 160, color: "#f1f5f9",
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "#1e1a3a" },
              "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#312e81" },
              "& .MuiSvgIcon-root": { color: "#475569" },
            }}
          >
            <MenuItem value="all">Tất cả</MenuItem>
            <MenuItem value={-1}>Chờ duyệt</MenuItem>
            <MenuItem value={0}>Thành viên</MenuItem>
            <MenuItem value={1}>Premium</MenuItem>
            <MenuItem value={2}>Quản trị</MenuItem>
          </Select>
          <Chip label={sortedLabel} size="small" variant="outlined" sx={{ color: "#6366f1", borderColor: "#312e81" }} />
        </Box>
      </Paper>

      {/* ---------- Table ---------- */}
      <Paper
        sx={{
          border: "1px solid #1e1a3a", borderRadius: 2, overflow: "hidden",
          background: "#0a0918",
        }}
      >
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress sx={{ color: "#6366f1" }} />
          </Box>
        ) : filteredUsers.length === 0 ? (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", py: 8, gap: 1 }}>
            <PeopleIcon sx={{ color: "#1e1a3a", fontSize: 48 }} />
            <Typography variant="body2" sx={{ color: "#333050" }}>
              Không tìm thấy người dùng nào
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              {/* ---------- Table Head ---------- */}
              <TableHead>
                <TableRow>
                  {["Người dùng", "Email"].map((h) => (
                    <TableCell key={h} sx={{
                      color: "#475569", fontWeight: 600, py: 1.5, px: 2,
                      fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: 0.8, borderBottom: "1px solid #1e1a3a",
                    }}>
                      {h}
                    </TableCell>
                  ))}
                  <TableCell sx={{ color: "#475569", fontWeight: 600, py: 1.5, px: 2,
                    fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: 0.8, borderBottom: "1px solid #1e1a3a",
                  }}>
                    <TableSortLabel active={orderByField === "role"} direction={orderByField === "role" ? order : "asc"}
                      onClick={() => {
                        if (orderByField === "role") setOrder((o) => (o === "asc" ? "desc" : "asc"));
                        else { setOrderBy("role"); setOrder("asc"); }
                      }}
                    >Role</TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ color: "#475569", fontWeight: 600, py: 1.5, px: 2,
                    fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: 0.8, borderBottom: "1px solid #1e1a3a",
                  }}>
                    <TableSortLabel active={orderByField === "createdAt"} direction={orderByField === "createdAt" ? order : "asc"}
                      onClick={() => {
                        if (orderByField === "createdAt") setOrder((o) => (o === "asc" ? "desc" : "asc"));
                        else { setOrderBy("createdAt"); setOrder("asc"); }
                      }}
                    >Ngày tạo</TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ color: "#475569", fontWeight: 600, py: 1.5, px: 2,
                    fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: 0.8, borderBottom: "1px solid #1e1a3a",
                  }}>
                    Đăng nhập cuối</TableCell>
                  <TableCell sx={{ color: "#475569", fontWeight: 600, py: 1.5, px: 2, textAlign: "right",
                    fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: 0.8, borderBottom: "1px solid #1e1a3a",
                  }}>Hành động</TableCell>
                </TableRow>
              </TableHead>

              {/* ---------- Table Body ---------- */}
              <TableBody>
                {filteredUsers.map((u) => {
                  const roleInfo = ROLE_LABELS[u.role] ?? { label: "?", color: "error" as const };
                  const roleCfg = ROLE_CONFIG[u.role] ?? ROLE_CONFIG[-1];
                  const isPending = u.role === -1;
                  const isSelf = u.uid === profile?.uid;

                  return (
                    <TableRow
                      key={u.uid}
                      sx={{
                        transition: "background 0.15s ease",
                        "&:nth-of-type(odd)": { background: "#0f0d1e" },
                        "&:nth-of-type(even)": { background: "#0a0918" },
                        "&:hover": { background: isPending ? "rgba(239,68,68,0.06)" : "#1a1735" },
                      }}
                    >
                      {/* User cell */}
                      <TableCell sx={{ py: 2, px: 2, borderBottom: "1px solid #12101f" }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                          <UserAvatar photoURL={u.photoURL} displayName={u.displayName || "U"} borderColor={isPending ? "#ef4444" : undefined} />
                          <Box>
                            <Typography variant="body2" sx={{ color: isSelf ? "#e2e8f0" : "#94a3b8", fontWeight: isSelf ? 600 : 400, lineHeight: 1.3 }}>
                              {u.displayName}
                              {isSelf && (
                                <Chip label="Bạn" size="small" sx={{ ml: 1, height: 18, fontSize: 0.6, color: "#a5b4fc", background: "rgba(99,102,241,0.12)" }} />
                              )}
                            </Typography>
                            {isPending && (
                              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                <Box sx={{ width: 5, height: 5, borderRadius: "50%", background: "#ef4444", animation: "pulse 2s infinite" }} />
                                <Typography variant="caption" sx={{ color: "#f87171", fontSize: 0.68 }}>
                                  Đang chờ duyệt
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </Box>
                      </TableCell>

                      {/* Email cell */}
                      <TableCell sx={{ py: 2, px: 2, borderBottom: "1px solid #12101f" }}>
                        <Typography variant="body2" sx={{ color: "#64748b", fontSize: "0.82rem", fontFamily: "'JetBrains Mono', 'Fira Code', monospace" }}>
                          {u.email}
                        </Typography>
                      </TableCell>

                      {/* Role cell */}
                      <TableCell sx={{ py: 2, px: 2, borderBottom: "1px solid #12101f" }}>
                        {isSelf ? (
                          <Chip
                            label={roleInfo.label} size="small" color={roleInfo.color}
                            sx={{
                              fontWeight: 500, borderRadius: 1,
                              background: roleCfg.bg, borderColor: roleCfg.border, borderWidth: 1, borderStyle: "solid",
                              boxShadow: `0 0 8px ${roleCfg.border}22`,
                            }}
                          />
                        ) : (
                          <Select size="small" value={u.role} onChange={(e) => handleRoleChange(u.uid, e.target.value as number)}
                            disabled={updating === u.uid}
                            sx={{
                              minWidth: 145, borderRadius: 1,
                              "& .MuiSelect-select": { py: 0.4, px: 1 },
                              "& .MuiOutlinedInput-notchedOutline": { borderColor: "#1e1a3a" },
                              "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#312e81" },
                              "& .MuiSvgIcon-root": { color: "#475569" },
                            }}
                          >
                            <MenuItem value={-1}>Chờ duyệt</MenuItem>
                            <MenuItem value={0}>Thành viên</MenuItem>
                            <MenuItem value={1}>Premium</MenuItem>
                            <MenuItem value={2}>Quản trị</MenuItem>
                          </Select>
                        )}
                      </TableCell>

                      {/* Date cell */}
                      <TableCell sx={{ py: 2, px: 2, borderBottom: "1px solid #12101f" }}>
                        <Typography variant="body2" sx={{ color: "#475569", fontSize: "0.8rem" }}>
                          {u.createdAt?.toDate?.()?.toLocaleDateString("vi-VN") ?? "—"}
                          {u.approvedAt && (
                            <Typography variant="caption" sx={{ display: "block", color: "#2a2650", mt: 0.3 }}>
                              <VerifiedIcon sx={{ fontSize: 12, verticalAlign: "middle", color: "#10b981" }} />
                              {" "}Duyệt: {u.approvedAt.toDate?.()?.toLocaleDateString("vi-VN") ?? "—"}
                            </Typography>
                          )}
                        </Typography>
                      </TableCell>

                      {/* Last login cell */}
                      <TableCell sx={{ py: 2, px: 2, borderBottom: "1px solid #12101f" }}>
                        {(() => {
                          const { label, color, expired } = formatLastLogin(u.lastLoginAt);
                          return (
                            <Typography variant="body2" sx={{ color, fontSize: "0.8rem", display: "flex", alignItems: "center", gap: 0.5 }}>
                              {expired && <Box sx={{ width: 6, height: 6, borderRadius: "50%", background: "#ef4444" }} />}
                              {label}
                            </Typography>
                          );
                        })()}
                      </TableCell>

                      {/* Action cell */}
                      <TableCell align="right" sx={{ py: 2, px: 2 }}>
                        {u.uid !== profile?.uid && (
                          <IconButton onClick={() => handleDelete(u.uid)} size="small"
                            sx={{
                              color: "#64748b", borderRadius: 1,
                              "&:hover": { background: "rgba(248,113,113,0.12)", color: "#f87171" },
                              transition: "all 0.15s ease",
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* ---------- Footer info ---------- */}
      {!loading && filteredUsers.length > 0 && (
        <Typography variant="caption" sx={{ color: "#2a2650", display: "block", mt: 2, textAlign: "center" }}>
          Hiển thị {filteredUsers.length} / {users.length} người dùng
        </Typography>
      )}

      {/* ---------- Snackbar ---------- */}
      <Snackbar open={snackbar.open} autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity} variant="filled">{snackbar.message}</Alert>
      </Snackbar>

      {/* ---------- Pulse ---------- */}
      <style>{`@keyframes pulse{0%,100%{opacity:1}50%{opacity:0.4}}`}</style>
    </Box>
  );
}
